<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :lockable="lockable"
            :has-lock="hasLock"
            :document-lock="documentLock"
            @force-takeover="attemptTakeover"
            @takeover="takeOver"

            :show-preview-button="shouldShowPreviewButton()"
            @click-preview="submitPreviewSite(form.language.iso, form.uri)"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :seo-data="data ? data.latest_seo : null"

            model="App\Models\AboutUsPage"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <base-page-fields
            class="mt-3"
            :form="form"
            :errors="errors"
            :editable-timestamps="true"
            :language="form.language"
            :publishable="publishable"
        ></base-page-fields>

        <input-rich-text
            name="overview"
            label="Intro Text (below H1)"
            v-model="form.overview"
            class="mt-2"
            :error="errors.overview ? errors.overview[0] : null"
            :maxlength=5000
            :language="form.language"
        />
        <p class="my-1 text-xs text-gray-600">You can use these available shortcodes in Intro Text (below H1) field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <div class="mt-2">
            <span class="text-gray-700">Content Blocks</span>
            <body-content
                :gallery="form.gallery"
                :language="form.language"
                v-model="form.contentBlocks"
                :document-class="className"
                :document-id="data ? data.id : null"
                :errors="errors"
                :content-section-with-how-to="contentSectionWithHowTo"
                :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                :content-sections-with-ranking="contentSectionsWithRanking"
                @add-content-block="addContentBlock"
                @add-content-section="addContentSection"
                @delete-content-block="deleteContentBlock"
                @delete-content-section="deleteContentSection"
                @load-content-blocks="loadContentBlocks"
                @add-how-to="howToAdded"
                @delete-how-to="howToDeleted"
                @add-bonus-list-top="bonusListTopAdded"
                @delete-bonus-list-top="bonusListTopDeleted"
                @add-bonus-list-bottom="bonusListBottomAdded"
                @delete-bonus-list-bottom="bonusListBottomDeleted"
                @add-ranking="rankingAdded"
                @delete-ranking="rankingDeleted"
            ></body-content>
        </div>

        <input-text
            name="featured_in_heading"
            label="Featured In Heading"
            v-model="form.featured_in_heading"
            class="mt-2"
            :error="
                errors.featured_in_heading
                    ? errors.featured_in_heading[0]
                    : null
            "
        />

        <div class="flex mt-2">
            <input-text
                name="featured_in_anchor"
                label="Featured In Heading Jump ID (excluding #)"
                v-model="form.featured_in_anchor"
                class="w-full mr-3"
                :error="
                    errors.featured_in_anchor
                        ? errors.featured_in_anchor[0]
                        : null
                "
            />

            <input-text
                name="featured_in_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.featured_in_anchor_text"
                class="w-full"
                :error="
                    errors.featured_in_anchor_text
                        ? errors.featured_in_anchor_text[0]
                        : null
                "
                :maxlength=255
            />
        </div>

        <media-reference
            v-model="form.logos"
            :language="form.language"
            class="mt-3"
            label="Logos"
            :media-types="['Logo']"
            multiple
        />

        <input-text
            name="team_heading"
            label="Team Heading"
            v-model="form.team_heading"
            class="mt-2"
            :error="errors.team_heading ? errors.team_heading[0] : null"
        />

        <div class="flex mt-2">
            <input-text
                name="team_anchor"
                label="Team Heading Jump ID (excluding #)"
                v-model="form.team_anchor"
                class="w-full mr-3"
                :error="errors.team_anchor ? errors.team_anchor[0] : null"
            />

            <input-text
                name="team_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.team_anchor_text"
                class="w-full"
                :error="
                    errors.team_anchor_text ? errors.team_anchor_text[0] : null
                "
                :maxlength=255
            />
        </div>

        <input-rich-text
            name="team_text"
            label="Team Text"
            v-model="form.team_text"
            class="mt-2"
            :error="errors.team_text ? errors.team_text[0] : null"
            :language="form.language"
        />

        <about-us-sections
            class="mt-3"
            v-model="form.sections"
            :errors="errors"
            :language="form.language"
        />

        <input-text
            name="our_works_heading"
            label="Our Works Heading"
            v-model="form.our_works_heading"
            class="mt-2"
            :error="
                errors.our_works_heading ? errors.our_works_heading[0] : null
            "
            :maxlength=255
        />

        <div class="flex mt-2">
            <input-text
                name="our_works_anchor"
                label="Our works Heading Jump ID (excluding #)"
                v-model="form.our_works_anchor"
                class="w-full mr-3"
                :error="
                    errors.our_works_anchor ? errors.our_works_anchor[0] : null
                "
                :maxlength=255
            />

            <input-text
                name="our_works_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.our_works_anchor_text"
                class="w-full"
                :error="
                    errors.our_works_anchor_text
                        ? errors.our_works_anchor_text[0]
                        : null
                "
                :maxlength=255
            />
        </div>

        <input-text
            name="history_heading"
            label="History Heading"
            v-model="form.history_heading"
            class="mt-2"
            :error="errors.history_heading ? errors.history_heading[0] : null"
        />

        <div class="flex mt-2">
            <input-text
                name="history_anchor"
                label="History Heading Jump ID (excluding #)"
                v-model="form.history_anchor"
                class="w-full mr-3"
                :error="errors.history_anchor ? errors.history_anchor[0] : null"
                :maxlength=255
            />

            <input-text
                name="history_anchor_text"
                label="Quick Navi Menu anchor text for Heading"
                v-model="form.history_anchor_text"
                class="w-full"
                :error="
                    errors.history_anchor_text
                        ? errors.history_anchor_text[0]
                        : null
                "
                :maxlength=255
            />
        </div>

        <input-rich-text
            name="history_text"
            label="History Text"
            v-model="form.history_text"
            class="mt-2"
            :error="errors.history_text ? errors.history_text[0] : null"
            :maxlength=5000
            :language="form.language"
        />
        <p class="my-1 text-xs text-gray-600">You can use these available shortcodes in History Text field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <about-us-timeline
            class="mt-3"
            :language="form.language"
            v-model="form.timeline"
            :errors="errors"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">FAQs</p>
            <reference
                create-text="Create new FAQ and link FAQ to this page"
                link-text="Link an existing FAQ"
                :language="form.language"
                :linkable="false"
                :editable="true"
                v-model="form.faq"
                :should-open-new-window=true
                model-data="FAQ"
            />
        </div>

        <contact-form
            :form="form"
            :errors="errors"
        >
        </contact-form>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import pageFormData from "../mixins/pageFormData";
import contentBlocksForm from "../mixins/contentBlocksForm";
import PageHeader from "./form/PageHeader";

export default {
    name: "AboutUsPageForm",
    components: {PageHeader},
    mixins: [crudForm, contentBlocksForm, pageFormData],
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                overview: this.getFormData("overview"),
                logos: this.getFormData("logos", []),
                faq: this.getFormData("faq"),
                faq_id: this.getFormData("faq_id"),
                team_text: this.getFormData("team_text"),
                team_heading: this.getFormData("team_heading"),
                sections: this.getFormData("sections", []),
                timeline: this.getFormData("timeline", []),
                history_text: this.getFormData("history_text"),
                contact_form_heading: this.getFormData("contact_form_heading"),
                featured_in_heading: this.getFormData("featured_in_heading"),
                history_heading: this.getFormData("history_heading"),
                our_works_heading: this.getFormData("our_works_heading"),
                team_anchor_text: this.getFormData("team_anchor_text"),
                history_anchor_text: this.getFormData("history_anchor_text"),
                featured_in_anchor_text: this.getFormData(
                    "featured_in_anchor_text"
                ),
                our_works_anchor_text: this.getFormData(
                    "our_works_anchor_text"
                ),
                contact_form_anchor_text: this.getFormData(
                    "contact_form_anchor_text"
                ),
                team_anchor: this.getFormData("team_anchor"),
                history_anchor: this.getFormData("history_anchor"),
                featured_in_anchor: this.getFormData("featured_in_anchor"),
                our_works_anchor: this.getFormData("our_works_anchor"),
                contact_form_anchor: this.getFormData("contact_form_anchor"),
                variants: this.getFormData('variants', []),
                created_at: this.getFormData("created_at") || new Date(),
                created_at_override: this.getFormData("created_at_override"),
                updated_at: this.getFormData("updated_at") || new Date(),
                updated_at_override: this.getFormData("updated_at_override"),
            },
        };
    },
    watch: {
        "form.faq": function (newValue) {
            this.form.faq_id = newValue ? newValue.id : null;
        },
    }
};
</script>
